import {once, mapValues, keyBy, compact, forEach} from 'lodash';
import {AdobeDTM} from 'modules/AdobeDTM';
import CONSTANTS from 'modules/utils/constants';
import {useCallback, useEffect, useRef, useState, useMemo, type RefObject} from 'react';
import {useParams, useNavigate} from 'react-router';
import {toggleBodyClass} from 'modules/utils/dom_helpers';
import type {IPlayer} from 'modules/actions';
import {useSelector} from 'react-redux';
import {
	getIsSuggestedPlayer,
	getIsMostSelectedPlayer,
	getMostSelectedPlayersByRoundId,
	getFavoritePlayersSelector, getSuggestedPlayersByRoundId,
} from 'modules/selectors';

export const useDigitParams = <T extends string[]>(...keys: T): Record<T[number], number> => {
	const navigate = useNavigate();
	const onlyDigits = /\d+/;
	const params = useParams<Record<string, string>>();

	const keysWithValue = compact(
		keys.map((key) => ({
			value: params[key],
			key,
		}))
	);

	const hasInvalidParams = keysWithValue.some(
		(routeParam) => routeParam.value && !onlyDigits.test(routeParam.value)
	);

	if (hasInvalidParams) {
		console.error(`The following params should be digit: ${keys.join(", ")}`);
	}

	useEffect(() => {
		if (hasInvalidParams) {
			navigate("../");
		}
	}, [hasInvalidParams, navigate]);

	const parsedValues = mapValues(keyBy(keysWithValue, "key"), (routeParam) => {
		return Number(routeParam.value);
	});

	return parsedValues as Record<T[number], number>;
};

export const useFixedBody = (isOpen: boolean, name: string) => {
	useEffect(() => {
		toggleBodyClass("fixed", isOpen, name);
		return () => {
			toggleBodyClass("fixed", false, name);
		};
	}, [isOpen, name]);
};

export const useMediaQuery = (query: string) => {
	const mediaMatch = window.matchMedia(query);
	const [matches, setMatches] = useState(mediaMatch.matches);

	useEffect(() => {
		const handler = (e: MediaQueryListEvent) => setMatches(e.matches);
		mediaMatch.addEventListener("change", handler);

		return () => {
			mediaMatch.removeEventListener("change", handler);
		};
	});

	return matches;
};

export const useIsMobile = () => {
	return useMediaQuery(CONSTANTS.MOBILE_SIZE_QUERY);
};

export const useAdobeDTM = (trackStateParams?: Parameters<(typeof AdobeDTM)["trackState"]>[0]) => {
	useEffect(() => {
		if (trackStateParams) {
			AdobeDTM.trackState({
				...trackStateParams,
				"page.type": "beat the streak",
			});
		}
		/* eslint-disable-next-line react-hooks/exhaustive-deps */
	}, []);
	/* eslint-disable-next-line @typescript-eslint/unbound-method,react-hooks/exhaustive-deps */
	const trackOnce = useCallback(once(AdobeDTM.trackState), []);
	return {
		trackAction: (p?: Parameters<(typeof AdobeDTM)["trackAction"]>[0]) =>
			p &&
			AdobeDTM.trackAction({
				"action.element.targetUrl": window.location.href,
				"action.element.index": 0,
				...p,
			}),
		trackState: (p?: Parameters<(typeof AdobeDTM)["trackState"]>[0]  ) =>
			p &&
			AdobeDTM.trackState({
				"page.type": "beat the streak",
				...p,
			}),
		onceTrackState: trackOnce,
	};
};

export const useInertModal = (ref: RefObject<HTMLDivElement>, isOpen: boolean, name: string) => {
	const prevActive = useRef(document.activeElement);
	const modal = ref.current;

	useEffect(() => {
		const root = document.getElementById("root");

		const setInert = (node: ParentNode | null, isOpenState: boolean) => {
			forEach(node?.parentNode?.children, (child) => {
				if (child !== node && isOpenState) {
					child.setAttribute("inert", "true");
				} else {
					child.removeAttribute("inert");
				}
			});

			if (node && node?.parentNode !== root) {
				setInert(node.parentNode, isOpenState);
			}
		};

		if (modal && root) {
			setInert(modal, isOpen);
			if (isOpen) {
				prevActive.current = document.activeElement;
				modal.querySelector("button")?.focus();
			} else {
				const prevFocus = prevActive.current as HTMLElement;
				prevFocus?.focus();
			}
		}

		return () => {
			setInert(modal, false);
			const prevFocus = prevActive.current as HTMLElement;
			prevFocus?.focus();
		};
	}, [isOpen, modal, name]);
};

export const useDialogModal = (ref: RefObject<HTMLDialogElement>, state: boolean) => {
	const activeElement = useRef<typeof document.activeElement>();
	const hasBeenFocused = useRef<boolean>(false);
	// Looks like we need to refactor this after upgrading to eslint 9
	// eslint-disable-next-line complexity
	useEffect(() => {
		if (state) {
			try {
				if (!ref.current?.open) {
					activeElement.current = document.activeElement;
					ref.current?.showModal();
				}
				if (!hasBeenFocused.current) {
					ref.current?.querySelector("button")?.focus();
					hasBeenFocused.current = true;
				}
			} catch (e) {
				console.info(e);
			}
		} else {
			if (typeof ref.current?.close === "function") {
				ref.current?.close();
			}
			(activeElement.current as HTMLElement)?.focus?.();
		}

		return () => {
			(activeElement.current as HTMLElement)?.focus?.();
		};
	}, [ref, state]);
};

export const useTrapFocus = (ref: RefObject<HTMLElement>, state: boolean) => {
	const activeElement = useRef<typeof document.activeElement>();
	const hasBeenFocused = useRef<boolean>(false);
	// Looks like we need to refactor this after upgrading to eslint 9
	// eslint-disable-next-line complexity
	useEffect(() => {
		if (state) {
			try {
				if (!hasBeenFocused.current) {
					activeElement.current = document.activeElement;
					ref.current?.querySelector("button")?.focus();
					hasBeenFocused.current = true;
				}
			} catch (e) {
				console.info(e);
			}
		} else {
			(activeElement.current as HTMLElement)?.focus?.();
		}

		return () => {
			(activeElement.current as HTMLElement)?.focus?.();
		};
	}, [ref, state]);
};

export const usePlayerElements = (player: IPlayer, roundId: number) => {
	const isSuggested = useSelector(getIsSuggestedPlayer)(roundId, player?.id);
	const isMostSelected = useSelector(getIsMostSelectedPlayer)(roundId, player?.id);
	const {favoritePlayerIds} = useSelector(getFavoritePlayersSelector);
	const isFavorite = !!favoritePlayerIds[ player?.id];

	return useMemo(() => {
		if(!player) {
			return null
		}
		return {
			...player,
			isSuggested,
			isMostSelected,
			isFavorite
		}
	}, [player, isSuggested, isMostSelected, isFavorite])
}

export const usePlayersElements = (players: IPlayer[], roundId: number) => {
	const suggestedPlayers = useSelector(getSuggestedPlayersByRoundId)(roundId);
	const mostSelectedPlayers = useSelector(getMostSelectedPlayersByRoundId)(roundId);
	const {favoritePlayerIds} = useSelector(getFavoritePlayersSelector);

	return useMemo(() => {
		return players?.map((player) => {
			return {
				...player,
				isSuggested: !!suggestedPlayers[ player.id ],
				isMostSelected: !!mostSelectedPlayers[ player.id ],
				isFavorite: !!favoritePlayerIds[ player.id]
			}
		})
	}, [favoritePlayerIds, mostSelectedPlayers, players, suggestedPlayers])
}


export const usePlayerPicksCountValue = ({playerId, roundId}: {
		playerId: number;
		roundId: number;
	}
) => {
	const mostSelectedPlayers = useSelector(getMostSelectedPlayersByRoundId)(roundId);

	const player = mostSelectedPlayers[ playerId ];

	if( !player ) {
		return null
	}
	return player.numberSelections
}